// @ts-check
// Agregações na tela (doc 06, seção 5.6): rótulos, formatação do valor, menu de operações por tipo de campo e a
// consulta ao groupBy sem campo de grupo (rodapé da tabela). O mesmo formato serve ao cabeçalho do kanban.
import { html } from 'htm/preact';
import { groupByRecords } from '../../data/rest.js';
import { FIELD_TYPE } from '../../lib/field-metadata.js';
import { formatCurrencyMicros, formatDate, formatDateTime, formatNumber } from '../../lib/format.js';
import { DropdownMenuItems, DropdownMenuSectionLabel, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { hasTranslation, t, translateMetadataLabel } from '../../i18n/index.js';
import {
  AGGREGATE_OPERATION_GROUPS,
  MAXIMUM_AGGREGATES_PER_REQUEST,
  buildAggregateParameter,
  chunkList,
  getAvailableAggregateOperations,
  isCountAggregate,
  isPercentageAggregate,
} from './view-layout.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {{ field: FieldMetadata, operation: string, subFieldName?: string | null }} AggregateRequest
 */

/** Rótulo completo da operação ("Soma", "Contar vazios"). @param {string} operation */
export const getAggregateLabel = (operation) => t(`boardTable.aggregates.${operation}`);

/** Rótulo curto do rodapé e do cabeçalho ("Soma", "Vazios", "% vazios"). @param {string} operation */
export const getAggregateShortLabel = (operation) => t(`boardTable.aggregatesShort.${operation}`);

/**
 * Valor da agregação já formatado: moeda, data, percentual ou número, conforme a operação e o campo.
 * @param {FieldMetadata} field @param {string} operation @param {unknown} value
 */
export function formatAggregateValue(field, operation, value) {
  if (value === null || value === undefined || value === '') return '-';
  if (isPercentageAggregate(operation)) return formatNumber(Number(value), { percentage: true, decimals: 0 });
  if (isCountAggregate(operation)) return formatNumber(Number(value));
  if (field.type === FIELD_TYPE.CURRENCY) return formatCurrencyMicros(Number(value), field.settings?.currencyCode ?? 'BRL', { short: true });
  if (field.type === FIELD_TYPE.DATE) return formatDate(String(value));
  if (field.type === FIELD_TYPE.DATE_TIME) return formatDateTime(String(value));
  return formatNumber(Number(value), { decimals: operation === 'AVG' ? undefined : field.settings?.decimals });
}

/**
 * Texto do cabeçalho do kanban: soma de número vira "12 alunos" quando o catálogo tem a unidade do campo; o resto
 * leva o rótulo curto e o campo ("Média de Valor: R$ 1,2 mil").
 * @param {FieldMetadata} field @param {string} operation @param {unknown} value
 */
export function formatBoardAggregate(field, operation, value) {
  const formatted = formatAggregateValue(field, operation, value);
  if (field.type === FIELD_TYPE.CURRENCY && operation === 'SUM') return formatted;
  if (operation === 'SUM' && (field.type === FIELD_TYPE.NUMBER || field.type === FIELD_TYPE.NUMERIC)) {
    const unitKey = `board.aggregateUnits.${field.name}`;
    if (hasTranslation(unitKey)) return t(unitKey, { count: Number(value) });
    return `${formatted} ${translateMetadataLabel(field.label).toLocaleLowerCase('pt-BR')}`;
  }
  return `${getAggregateShortLabel(operation)} ${translateMetadataLabel(field.label).toLocaleLowerCase('pt-BR')}: ${formatted}`;
}

/**
 * Valores das agregações do conjunto filtrado, na ordem pedida; pedidos de até 5 por vez, como a rota aceita.
 * @param {ObjectMetadata} objectMetadata @param {any} filter @param {AggregateRequest[]} requests
 * @param {string | null} [drilldown] lista aberta por um clique num gráfico
 * @returns {Promise<{ count: number, values: unknown[] }>}
 */
export async function fetchAggregateValues(objectMetadata, filter, requests, drilldown = null) {
  const chunks = chunkList(requests, MAXIMUM_AGGREGATES_PER_REQUEST);
  const results = await Promise.all((chunks.length > 0 ? chunks : [[]]).map((chunk) => groupByRecords(objectMetadata, {
    groupByFieldName: '',
    filter,
    drilldown,
    aggregate: chunk.map((request) => buildAggregateParameter(request.field, request.operation, request.subFieldName)).join(',') || null,
  })));
  const count = Number(results[0]?.[0]?.count ?? 0);
  const values = results.flatMap((groups, index) => {
    const group = groups[0];
    const size = chunks[index]?.length ?? 0;
    return Array.from({ length: size }, (_unused, valueIndex) => group?.aggregateValues?.[valueIndex] ?? (valueIndex === 0 ? group?.aggregateValue ?? null : null));
  });
  return { count, values };
}

/**
 * Lista de operações do campo, por grupo (contagem, percentual, valor), com "Nenhuma" opcional no topo.
 * @param {{ field: FieldMetadata, selected?: string | null, onSelect: (operation: string | null) => void, allowNone?: boolean, excluded?: string[] }} props
 */
export function AggregateOperationMenu({ field, selected = null, onSelect, allowNone = true, excluded = [] }) {
  const available = new Set(getAvailableAggregateOperations(field).filter((operation) => !excluded.includes(operation)));
  const sections = AGGREGATE_OPERATION_GROUPS.map((group) => ({ key: group.key, operations: group.operations.filter((operation) => available.has(operation)) }))
    .filter((section) => section.operations.length > 0);
  return html`<${DropdownMenuItems}>
    ${allowNone ? html`<${MenuItem} icon="IconCircleOff" text=${t('boardTable.aggregateNone')} selected=${!selected} onClick=${() => onSelect(null)} />` : null}
    ${sections.map((section, index) => html`
      ${index > 0 || allowNone ? html`<${DropdownMenuSeparator} />` : null}
      <${DropdownMenuSectionLabel} label=${t(`boardTable.aggregateGroups.${section.key}`)} />
      ${section.operations.map((operation) => html`<${MenuItem}
        key=${operation}
        text=${getAggregateLabel(operation)}
        selected=${selected === operation}
        onClick=${() => onSelect(operation)}
      />`)}
    `)}
  <//>`;
}
